import React, { useState, useCallback } from "react";
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Paper,
  Fade,
  IconButton,
  useTheme,
} from "@mui/material";
import {
  CloudUpload,
  CheckCircleOutline,
  Close as CloseIcon,
  FileUpload,
} from "@mui/icons-material";
import mammoth from "mammoth";
// No need for XLSX import since we're using a different approach for PowerPoints
const PDF_WORKER_URL = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js`;

const FileUploader = ({
  onFileContent,
  styles,
  forQuiz,
  forFlashcards,
  forPowerPoint,
  forTextlabbet,
}) => {
  const [uploading, setUploading] = useState(false);
  const [uploaded, setUploaded] = useState(false);
  const [fileName, setFileName] = useState("");
  const [error, setError] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const processFile = async (file) => {
    if (!file) return;

    setError("");
    setUploading(true);
    await new Promise((resolve) => setTimeout(resolve, 1000));
    setFileName(file.name);

    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const arrayBuffer = e.target.result;
        let content = "";

        // Handle different file types
        const fileType = file.name.toLowerCase();

        if (fileType.endsWith(".docx") || fileType.endsWith(".doc")) {
          const result = await mammoth.extractRawText({ arrayBuffer });
          content = result.value;
        } else if (fileType.endsWith(".pptx") || fileType.endsWith(".ppt")) {
          // Extract text from PowerPoint by identifying slide content directly
          const data = new Uint8Array(arrayBuffer);
          const text = new TextDecoder("utf-8").decode(data);

          // Find actual content text in slides by targeting specific XML patterns
          const slideContents = [];

          // Match text between <a:t> tags (slide text content in PPTX format)
          const textMatches = text.match(/<a:t>([^<]+)<\/a:t>/g) || [];
          for (const match of textMatches) {
            const content = match.replace(/<a:t>|<\/a:t>/g, "");
            if (content.trim().length > 0) {
              slideContents.push(content.trim());
            }
          }

          // If no text was found with <a:t> tags, try a broader approach
          if (slideContents.length === 0) {
            // Look for any reasonable text content
            const fallbackMatches = text.match(/>[^<]{5,}</g) || [];
            for (const match of fallbackMatches) {
              const content = match.substring(1, match.length - 1).trim();
              if (
                content.length > 0 &&
                !content.includes("xmlns:") &&
                !content.includes("http://")
              ) {
                slideContents.push(content);
              }
            }
          }

          content = slideContents.join("\n");

          // If we still don't have content, use a very simple approach
          if (content.trim().length === 0) {
            content =
              "No text content could be extracted from this PowerPoint file.";
          }
        } // Add this to your existing processFile function in the file type handling section
        else if (fileType.endsWith(".pdf")) {
          try {
            // Load the PDF.js library from CDN
            const pdfjsLib = await new Promise((resolve) => {
              const script = document.createElement("script");
              script.src =
                "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js";
              script.onload = () => {
                // Set worker source once the library is loaded
                window.pdfjsLib.GlobalWorkerOptions.workerSrc =
                  "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js";
                resolve(window.pdfjsLib);
              };
              document.head.appendChild(script);
            });

            // Load the PDF document
            const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
            const pdf = await loadingTask.promise;

            // Get total number of pages
            const numPages = pdf.numPages;
            const textContent = [];

            // Extract text from each page
            for (let i = 1; i <= numPages; i++) {
              const page = await pdf.getPage(i);
              const text = await page.getTextContent();

              // Concatenate text items with spaces
              const pageText = text.items.map((item) => item.str).join(" ");

              textContent.push(pageText);
            }

            // Join all pages with line breaks
            content = textContent.join("\n\n");
          } catch (error) {
            console.error("Error processing PDF:", error);
            setError("Det gick inte att läsa PDF-filen. Försök igen.");
            setUploading(false);
          }
        } else {
          // Plain text files
          content = await new Promise((resolve) => {
            const textReader = new FileReader();
            textReader.onload = (e) => resolve(e.target.result);
            textReader.readAsText(file);
          });
        }

        onFileContent(content);

        setUploading(false);
        setUploaded(true);
      };

      reader.readAsArrayBuffer(file);
    } catch (error) {
      console.error("Error processing file:", error);
      setError("Det gick inte att läsa filen. Försök igen.");
      setUploading(false);
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    processFile(file);
  };

  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();

    // Get the bounding rectangle of the drop zone
    const rect = e.currentTarget.getBoundingClientRect();

    // Check if the cursor is outside the drop zone
    if (
      e.clientX < rect.left ||
      e.clientX >= rect.right ||
      e.clientY < rect.top ||
      e.clientY >= rect.bottom
    ) {
      setIsDragging(false);
    }
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const file = e.dataTransfer.files[0];
    if (file) {
      // Check if file type is supported
      const isSupported = [
        ".docx",
        ".doc",
        ".txt",
        ".pptx",
        ".ppt",
        ".pdf",
      ].some((ext) => file.name.toLowerCase().endsWith(ext));

      if (!isSupported) {
        setError(
          "Filformatet stöds inte. Använd .pdf, .docx, .doc, .txt, .pptx eller .ppt",
        );
        return;
      }

      // Check file size with different limits based on file type
      const isPowerPoint =
        file.name.toLowerCase().endsWith(".pptx") ||
        file.name.toLowerCase().endsWith(".ppt");
      const maxSize = isPowerPoint
        ? 20 * 1024 * 1024 // 20MB for PowerPoint
        : 20 * 1024 * 1024; // 20MB for other files

      if (file.size > maxSize) {
        const sizeLimit = isPowerPoint ? "20MB" : "20MB";
        setError(`Filen är för stor. Maximal storlek är ${sizeLimit}`, "error");
        return;
      }

      processFile(file);
    }
  }, []);

  const uploadBox = {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "300px",
    width: "100%",
    borderRadius: "8px",
    backgroundColor: isDragging
      ? isDark
        ? "rgba(107, 33, 168, 0.1)"
        : "rgba(237, 233, 254, 0.5)"
      : isDark
        ? "rgba(88, 28, 135, 0.08)"
        : "rgba(245, 243, 255, 0.3)",
    border: "2px dashed",
    borderColor: isDragging
      ? isDark
        ? "rgba(139, 92, 246, 0.6)"
        : "rgba(109, 40, 217, 0.5)"
      : isDark
        ? "rgba(139, 92, 246, 0.35)"
        : "rgba(167, 139, 250, 0.4)",
    boxShadow: isDragging ? "0 2px 6px rgba(139, 92, 246, 0.15)" : "",
    transition: "all 0.2s ease",
    "&:hover": {
      borderColor: isDark
        ? "rgba(124, 58, 237, 0.6)"
        : "rgba(109, 40, 217, 0.5)",
      backgroundColor: isDark
        ? "rgba(107, 33, 168, 0.1)"
        : "rgba(237, 233, 254, 0.4)",
      boxShadow: "0 3px 8px rgba(139, 92, 246, 0.12)",
    },
  };

  const successBox = {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "300px",
    width: "100%",
    borderRadius: "16px",
    bgcolor: isDark ? "rgba(46, 125, 50, 0.15)" : "rgba(46, 125, 50, 0.08)",
    transition: "all 0.3s ease-in-out",
  };

  return (
    <Box sx={{ width: "100%", maxWidth: "800px", mx: "auto", p: 3 }}>
      <input
        type="file"
        accept=".docx,.txt,.doc,.pptx,.ppt,.pdf"
        onChange={handleFileUpload}
        style={{ display: "none" }}
        id="file-upload-input"
      />

      <Fade in={true}>
        <Box>
          {!uploaded ? (
            <label
              htmlFor="file-upload-input"
              style={{ cursor: "pointer", width: "100%" }}
            >
              <Box
                sx={uploadBox}
                onDragEnter={handleDragEnter}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                {uploading ? (
                  <>
                    <CircularProgress size={60} sx={{ mb: 3 }} />
                    <Typography variant="h6" sx={{ mb: 1, fontWeight: 500 }}>
                      Bearbetar fil...
                    </Typography>
                  </>
                ) : (
                  <>
                    <CloudUpload
                      sx={{
                        fontSize: 60,
                        color: isDragging
                          ? theme.palette.primary.main
                          : theme.palette.primary.main,
                        mb: 3,
                        transform: isDragging ? "scale(1.1)" : "scale(1)",
                        transition: "transform 0.2s ease-in-out",
                      }}
                    />
                    <Typography
                      variant="h6"
                      sx={{ mb: 1, fontWeight: isDragging ? 700 : 500 }}
                    >
                      {isDragging ? "Släpp filen här" : "Dra och släpp fil här"}
                    </Typography>
                    <Typography color="text.secondary" sx={{ mb: 3 }}>
                      eller
                    </Typography>
                    <Button
                      variant="purp"
                      startIcon={<FileUpload />}
                      sx={{ ...styles.tightButton }}
                      onClick={() =>
                        document.getElementById("file-upload-input").click()
                      }
                    >
                      Välj fil
                    </Button>
                  </>
                )}
              </Box>
            </label>
          ) : (
            <Box sx={successBox}>
              <CheckCircleOutline
                sx={{ fontSize: 60, color: "success.main", mb: 3 }}
              />
              <Typography
                variant="h6"
                sx={{
                  color: "success.main",
                  fontWeight: 500,
                  textAlign: "center",
                  width: "80%",
                  margin: "0 auto",
                  mb: 2,
                }}
              >
                Du har nu laddat upp filen och kan fortsätta med att{" "}
                {forTextlabbet ? " bearbeta" : "generera"}{" "}
                {forQuiz
                  ? "ditt quiz"
                  : forFlashcards
                    ? "dina flashcards"
                    : forPowerPoint
                      ? "din PowerPoint"
                      : forTextlabbet
                        ? "din text"
                        : "din lektion"}{" "}
                nedan.
              </Typography>
              <Typography color="text.secondary" sx={{ mb: 3 }}>
                {fileName}
              </Typography>
              <Button
                variant="purp"
                onClick={() => {
                  setUploaded(false);
                  setFileName("");
                  document.getElementById("file-upload-input").click();
                }}
              >
                Ladda upp en annan fil
              </Button>
            </Box>
          )}

          {error && (
            <Typography
              color="error"
              sx={{
                display: "block",
                textAlign: "center",
                mt: 2,
              }}
            >
              {error}
            </Typography>
          )}

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: "block",
              textAlign: "center",
              mt: 2,
            }}
          >
            Stödda filformat: .docx, .doc, .txt, .pptx, .ppt, .pdf
          </Typography>
        </Box>
      </Fade>
    </Box>
  );
};

export default FileUploader;
